import React from "react";
import { useTranslationStore } from "../../../state/useTranslationStore";
import AddLanguageButton from "./AddLanguageButton";
import { toast } from "sonner";

const HijraMonthsEditor: React.FC = () => {
  const {
    hijra_months,
    activeLanguage,
    availableLanguages,
    loading,
    selectLanguage,
    updateTranslation,
    saveTranslations,
    addNewLanguage,
  } = useTranslationStore();

  // Filter Hijra months for the active language with original index tracking
  const activeHijraMonths = hijra_months
    .map((month, idx) => ({ ...month, originalIndex: idx }))
    .filter((month) => month.language_code === activeLanguage);

  // Handle input changes
  const handleInputChange = (originalIndex: number, value: string) => {
    if (originalIndex >= 0 && originalIndex < hijra_months.length) {
      updateTranslation("hijra_months", originalIndex, value);
    } else {
      console.error("Invalid index for hijra_months");
    }
  };

  // Handle saving translations
  const handleSave = async () => {
    try {
      await saveTranslations();
      toast.success("Hijra months updated successfully!");
    } catch (error) {
      console.error("Error saving Hijra months:", error);
      toast.error("Failed to save Hijra months.");
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-purple-600">Edit Hijra Months</h1>

      {/* Language Selection */}
      <div className="mb-4 flex gap-4 flex-wrap">
        <div className="space-x-3">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={`px-3 py-2 rounded-md focus:outline-none focus:ring-2 capitalize ${
                activeLanguage === lang.code
                  ? "bg-purple-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {lang.language}
            </button>
          ))}
        </div>
        <AddLanguageButton
          data={hijra_months}
          handlerAddLangauge={addNewLanguage}
          disabled={loading}
        />
      </div>

      {/* Translation Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeHijraMonths.map((month) => (
          <div
            key={`${month.original_text}-${month.language_code}`}
            className="flex flex-col gap-2"
          >
            <label className="text-gray-700 font-medium">
              {month.original_text} ({month.language_code})
            </label>
            <input
              type="text"
              value={month.translated_text}
              onChange={(e) =>
                handleInputChange(month.originalIndex, e.target.value)
              }
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={loading}
            />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default HijraMonthsEditor;
